// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { prevPurchasedTag } from "@conklin/App/Hooks/xUseWidgetBadges";
import { TextBadge } from "@conklin/MobiusOverrides/Badge/Badge";
import { color, mergeWithCss } from "@conklin/XDL/Utilities";
import useOverlayBadge from "@insite/client-framework/Common/Hooks/useOverlayBadge";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { css } from "styled-components";

interface Props {
    productContext: ProductContextModel;
    showTextBadge?: boolean;
    showImageBadge?: boolean;
    styles?: { badgeTextWrapper?: InjectableCss; badgeImageWrapper?: InjectableCss };
    isPrev?: boolean;
}

const xUseOverlayBadge = ({ productContext, showTextBadge, showImageBadge, styles, isPrev }: Props) => {
    const isPreviouslyPurchased = (productContext.product as any).previouslyPurchased && isPrev;

    const {
        showOverlayBadge,
        overlayBadge,
        overlayBadgeWrapperStyles,
        badgeAreaWrapperRef,
        overlayBadgeWrapperClickHandler,
    } = useOverlayBadge({
        productContext: {
            product: {
                badges: [
                    ...(isPreviouslyPurchased ? [prevPurchasedTag] : []),
                    ...(productContext.product.badges || []),
                ],
            },
        } as any,
        showTextBadge,
        showImageBadge,
        styles: {
            badgeImageWrapper: {
                ...styles?.badgeImageWrapper,
            },
            badgeTextWrapper: {
                ...(isPreviouslyPurchased
                    ? mergeWithCss(
                          {
                              css: css`
                                  &&& {
                                      position: absolute;
                                      bottom: 4px;
                                      left: 4px;
                                      height: fit-content;
                                      top: unset;
                                  }
                                  ${TextBadge} {
                                      outline-color: ${color("primary")};
                                      border-color: ${color("common.white")};
                                      color: ${color("common.white")};
                                      background-color: ${color("primary")};
                                  }
                              `,
                          },
                          styles?.badgeTextWrapper,
                      )
                    : styles?.badgeTextWrapper),
            },
        },
    });

    return {
        showOverlayBadge,
        overlayBadge,
        overlayBadgeWrapperStyles,
        badgeAreaWrapperRef,
        overlayBadgeWrapperClickHandler,
    };
};

export default xUseOverlayBadge;
